import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { PARTNER_SLUG } from '@/shared/lib/partner';
import type { Placement } from '@/shared/canvas/types';

export interface CartItem {
  id: string;
  productId: string;
  title: string;
  color: string;
  size: string;
  qty: number;
  unitPriceRub: number;
  previewUrl?: string;
  /** Готовый принт изделия. Пусто — изделие без принта, и рисовать нечего. */
  printUrl?: string;
  /**
   * Позиция собрана в конструкторе. Только у таких в миниатюре стоит
   * собранный принт: на готовом изделии он был бы чужим рисунком,
   * которого человек не выбирал.
   */
  isCustom?: boolean;
  /**
   * Сторона, на которой печатаем. Часть позиции, а не вида: перёд
   * и спина печатаются по-разному, и две одинаковые вещи с разными
   * сторонами — это две разные строки заказа.
   */
  printSide?: 'front' | 'back';
  placements: Array<Placement & { printId: string; area: string }>;
}

interface CartState {
  items: CartItem[];
  add: (item: Omit<CartItem, 'id'>) => void;
  remove: (id: string) => void;
  setQty: (id: string, qty: number) => void;
  clear: () => void;
  total: () => number;
  count: () => number;
}

export const useCartStore = create<CartState>()(
  // Корзина переживает перезагрузку страницы: пользователь не потеряет
  // собранный мерч, если сеть моргнула или он случайно закрыл вкладку.
  persist(
    (set, get) => ({
      items: [],

      // Одинаковые позиции складываются, а не множатся строками.
      //
      // Раньше повторное добавление той же вещи давало вторую строку с
      // единицей, и в корзине оказывались две «Футболка XS» подряд. С
      // выбором нескольких размеров разом это стало заметно сразу.
      add: (item) =>
        set((state) => {
          const key = signature(item);
          const existing = state.items.find((current) => signature(current) === key);

          if (existing) {
            return {
              items: state.items.map((current) =>
                current.id === existing.id ? { ...current, qty: current.qty + item.qty } : current,
              ),
            };
          }

          return { items: [...state.items, { ...item, id: crypto.randomUUID() }] };
        }),

      remove: (id) => set((state) => ({ items: state.items.filter((i) => i.id !== id) })),

      setQty: (id, qty) =>
        set((state) => ({
          items: state.items.map((i) => (i.id === id ? { ...i, qty: Math.max(1, qty) } : i)),
        })),

      clear: () => set({ items: [] }),

      total: () => get().items.reduce((sum, i) => sum + i.unitPriceRub * i.qty, 0),

      count: () => get().items.reduce((sum, i) => sum + i.qty, 0),
    }),
    // Ключ привязан к заказчику. Корзина с общим ключом переезжала между
    // витринами: собранная у одного заказчика вещь всплывала в корзине
    // другого — с чужим названием, чужой ценой и чужим изделием.
    { name: `ls-cart:${PARTNER_SLUG}` },
  ),
);

/**
 * Признак «та же самая вещь»: товар, цвет, размер и раскладка принтов.
 *
 * Раскладка входит в признак, потому что две футболки одного размера с
 * разными принтами — это две разные позиции, а не количество одной.
 */
function signature(item: Omit<CartItem, 'id'>): string {
  const design = item.placements
    .map((p) => `${p.printId}:${p.area}:${round(p.x)}:${round(p.y)}:${round(p.width)}:${p.flipped ?? false}`)
    .join('|');

  // Своя сборка и готовое изделие — разные позиции, даже если это та же
  // футболка того же цвета и размера: печатают на них разное.
  const print = item.isCustom ? 'custom' : (item.printUrl ?? '');

  // Сторона печати входит в признак: та же вещь с принтом спереди
  // и та же со спиной — две разные позиции, и складывать их в одну
  // строку значит напечатать половину заказа не там.
  const side = item.printSide ?? 'front';

  return `${item.productId}|${item.color}|${item.size}|${print}|${side}|${design}`;
}

// Координаты — дробные числа после перетаскивания мышью. Сравнивать их
// как есть бессмысленно: разница в одну миллионную не видна ни на экране,
// ни на изделии, а строки уже разные.
function round(value: number): number {
  return Math.round(value * 1000) / 1000;
}
